import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import { ArrowLeft, Star, Send, Clock, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";

interface BlogLayoutProps {
  children: React.ReactNode;
  heroImage?: string;
  heroAlt?: string;
  title: string;
  category: string;
  readTime: string;
  date: string;
}

const BlogLayout = ({ children, heroImage, heroAlt, title, category, readTime, date }: BlogLayoutProps) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [hasRated, setHasRated] = useState(false);
  const [comments, setComments] = useState<{ name: string; text: string; date: string }[]>([]);
  const [commentName, setCommentName] = useState("");
  const [commentText, setCommentText] = useState("");

  const handleRate = (value: number) => {
    setRating(value);
    setHasRated(true);
  };

  const handleComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentName.trim() || !commentText.trim()) return;
    setComments(prev => [...prev, {
      name: commentName.trim(),
      text: commentText.trim(),
      date: new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }),
    }]);
    setCommentName("");
    setCommentText("");
  };

  return (
    <main className="min-h-screen bg-background relative">
      <AnimatedBackground />
      <Navigation />

      {/* Hero Section with Background Image */}
      {heroImage && (
        <section className="relative h-[50vh] md:h-[70vh] flex items-end overflow-hidden">
          <img
            src={heroImage}
            alt={heroAlt || title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/30 to-charcoal/10" />
          
          <div className="relative z-10 container px-4 md:px-6 pb-8 md:pb-16">
            <div className="max-w-3xl mx-auto">
              <Link
                to="/nuernberg/blog"
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Alle Artikel
              </Link>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/20 backdrop-blur-sm text-white border border-white/10">
                  {category}
                </span>
                <span className="flex items-center gap-1 text-xs text-white/70">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {date}
                </span>
                <span className="flex items-center gap-1 text-xs text-white/70">
                  <Clock className="w-3.5 h-3.5" />
                  {readTime} Lesezeit
                </span>
              </div>

              <h1 className="text-2xl md:text-5xl lg:text-display-lg font-serif font-light text-white leading-tight">
                {title}
              </h1>
            </div>
          </div>
        </section>
      )}

      {/* Article Content */}
      <section className="py-10 md:py-24 relative z-10">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none
              prose-headings:font-serif prose-headings:font-light prose-headings:text-foreground
              prose-h2:text-heading prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-muted-foreground prose-p:font-light prose-p:leading-relaxed
              prose-a:text-highlight prose-a:font-medium prose-a:no-underline hover:prose-a:underline
              prose-li:text-muted-foreground prose-li:font-light
              prose-strong:text-foreground prose-strong:font-medium
              prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground prose-blockquote:font-light
            ">
              {children}
            </div>

            {/* Rating Section */}
            <div className="mt-16 pt-10 border-t border-border">
              <h3 className="text-lg font-serif font-medium text-foreground mb-3">
                Wie hilfreich war dieser Artikel?
              </h3>
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleRate(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-0.5 transition-transform hover:scale-110"
                    disabled={hasRated}
                    aria-label={`${star} Sterne`}
                  >
                    <Star
                      className={`w-6 h-6 transition-colors ${
                        (hoverRating || rating) >= star
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-border"
                      }`}
                    />
                  </button>
                ))}
              </div>
              {hasRated && (
                <p className="text-sm text-muted-foreground">
                  Danke für deine Bewertung! ({rating}/5 Sterne)
                </p>
              )}
            </div>

            {/* Comments Section */}
            <div className="mt-12 pt-10 border-t border-border">
              <h3 className="text-lg font-serif font-medium text-foreground mb-6">
                Kommentare ({comments.length})
              </h3>

              {comments.length > 0 && (
                <div className="space-y-6 mb-10">
                  {comments.map((c, i) => (
                    <div key={i} className="p-5 rounded-2xl bg-secondary/50 border border-border/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground">{c.name}</span>
                        <span className="text-xs text-muted-foreground">{c.date}</span>
                      </div>
                      <p className="text-sm text-muted-foreground font-light">{c.text}</p>
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleComment} className="space-y-4">
                <input
                  type="text"
                  placeholder="Dein Name"
                  value={commentName}
                  onChange={(e) => setCommentName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <textarea
                  placeholder="Schreibe einen Kommentar..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
                <Button
                  type="submit"
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-blush-dark rounded-full px-6 font-medium"
                >
                  <Send className="w-3.5 h-3.5 mr-2" />
                  Kommentar senden
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default BlogLayout;
